import { useUserDeviceEventsStream } from '@/api';
import { DownArrowIcon, RightArrowIcon, UpArrowIcon } from '@/Components/Common/Icons';
import { useDashboardModal } from '@/Components/Dashboard/DashboardModal';
import { TemperatureIcon } from '@/Components/Dashboard/icons';
import {
  DEVICE_EVENT_TYPES,
  DEVICE_EVENTS_PROPERTY,
  DEVICE_STATE_PROPERTY,
  DEVICE_TYPES,
  DEVICE_UNITS,
  EVENT_PROPERTIES,
  EVENT_TYPES,
  SENSOR_PROPERTY,
  USER_MEASUREMENTS,
} from '@/constants/device';
import { translator } from '@vantiva-sa/homesight_humaui/Store/configStore';
import { Card } from 'antd';
import { get, isArray, isString, last, maxBy } from 'lodash';
import PropTypes from 'prop-types';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import '../RecentActivity.less';
import {
  convertTemperature,
  formatTime,
  getAccessoryUuids,
  getDateLabel,
  HistoryModalTable,
  LoadingCard,
  RECENT_ACTIVITY_HISTORY_DAYS,
  temperatureUnits,
} from '../RecentActivityUtils';

const deviceTypes = [DEVICE_TYPES.ENVIRONMENTAL_SENSOR, DEVICE_TYPES.TEMPERATURE_SENSOR];
// The card and its modal only display temperature, so skip battery/humidity/light events to keep the payload small.
const TEMPERATURE_EVENT_TYPES = [
  EVENT_TYPES.ENVIRONMENTAL_SENSOR.SENSOR_TEMPERATURE_REPORT,
  EVENT_TYPES.TEMPERATURE_HUMIDITY_SENSOR.ACCESSORY_STATE_BLE_TEMPERATURE_SENSOR,
];
// Device types whose accessories report one of the temperature event types.
const TEMPERATURE_DEVICE_TYPES = new Set(
  deviceTypes.filter((type) =>
    DEVICE_EVENT_TYPES[type]?.some((eventType) => TEMPERATURE_EVENT_TYPES.includes(eventType))
  )
);

const isTemperatureEvent = (event) =>
  TEMPERATURE_EVENT_TYPES.includes(get(event, EVENT_PROPERTIES.MESSAGE_TYPE, ''));

// Where the temperature value and unit live, per event type. Latest-update events (card) use
// flattened `<eventtype>__<field>` keys; v2 history events (modal) use the raw field names.
const LATEST_EVENT_TEMPERATURE_FIELDS = {
  [EVENT_TYPES.ENVIRONMENTAL_SENSOR.SENSOR_TEMPERATURE_REPORT]: {
    value: SENSOR_PROPERTY.ENVIRONMENTAL_SENSOR.TEMPERATURE,
    unit: DEVICE_STATE_PROPERTY.temperature_sensor_unit_2,
  },
  [EVENT_TYPES.TEMPERATURE_HUMIDITY_SENSOR.ACCESSORY_STATE_BLE_TEMPERATURE_SENSOR]: {
    value: DEVICE_STATE_PROPERTY.temperature_sensor,
    unit: DEVICE_STATE_PROPERTY.temperature_sensor_unit,
  },
};
const HISTORY_EVENT_TEMPERATURE_FIELDS = {
  [EVENT_TYPES.ENVIRONMENTAL_SENSOR.SENSOR_TEMPERATURE_REPORT]: {
    value: DEVICE_EVENTS_PROPERTY.sensor_temperature_report,
    unit: DEVICE_EVENTS_PROPERTY.sensor_temperature_report_temperature_unit,
  },
  [EVENT_TYPES.TEMPERATURE_HUMIDITY_SENSOR.ACCESSORY_STATE_BLE_TEMPERATURE_SENSOR]: {
    value: DEVICE_EVENTS_PROPERTY.accessory_state_ble_temperature_sensor,
    unit: DEVICE_EVENTS_PROPERTY.accessory_state_ble_temperature_sensor_temperature_unit,
  },
};

// Returns the event's temperature converted to `measurementSystem`, with its unit suffix.
const getTemperatureLabel = (event, fieldsByEventType, measurementSystem) => {
  const fields = fieldsByEventType[get(event, EVENT_PROPERTIES.MESSAGE_TYPE, '')];
  let temperature = null;
  let currentSystem = USER_MEASUREMENTS.METRIC;
  if (fields) {
    // Environmental sensors report several observations ("26.0,26,26.02"); the last is the latest.
    const raw = get(event, fields.value, '');
    temperature = isArray(raw) ? last(raw) : isString(raw) ? last(raw.split(',')) : raw;
    currentSystem =
      get(event, fields.unit, DEVICE_UNITS.TEMPERATURE.C) === DEVICE_UNITS.TEMPERATURE.C
        ? USER_MEASUREMENTS.METRIC
        : USER_MEASUREMENTS.IMPERIAL;
  }
  const converted = convertTemperature(temperature, currentSystem, measurementSystem);
  return `${converted}${temperatureUnits[measurementSystem]}`;
};

// Cells after the date cell (module level, so the shared table's rows stay memoized).
const renderCells = (entry, { measurementSystem }) => (
  <>
    <div className="row-cell bp-cell">{get(entry, EVENT_PROPERTIES.LOCATION, '-')}</div>
    <div className="row-cell bp-cell">
      {getTemperatureLabel(entry, HISTORY_EVENT_TEMPERATURE_FIELDS, measurementSystem)}
    </div>
  </>
);

// Streams the temperature history from the v2 API (newest first) and renders rows as they arrive.
const ModalContent = ({ useruuid, deviceUuids, ...displayProps }) => {
  const trans = useSelector(translator);
  const history = useUserDeviceEventsStream({
    useruuid,
    deviceuuid: deviceUuids,
    eventTypes: TEMPERATURE_EVENT_TYPES,
    days: RECENT_ACTIVITY_HISTORY_DAYS,
  });

  return (
    <HistoryModalTable
      {...displayProps}
      {...history}
      headers={[trans('DATE_L'), trans('LOCATION'), trans('INDOORTEMPERATURE')]}
      renderCells={renderCells}
    />
  );
};

ModalContent.propTypes = {
  useruuid: PropTypes.string,
  deviceUuids: PropTypes.arrayOf(PropTypes.string).isRequired,
};

export const SmartHomeEnvironmentalCard = (props) => {
  const {
    useruuid,
    timezone,
    locale,
    is24HourFormat,
    measurementSystem,
    accessories,
    data,
    isLoading,
    trendData,
  } = props;
  const trans = useSelector(translator);
  const { openDashboardModal } = useDashboardModal();

  // One request covers every temperature-capable sensor; the API accepts a list of device UUIDs.
  const deviceUuids = useMemo(
    () => getAccessoryUuids(accessories, TEMPERATURE_DEVICE_TYPES),
    [accessories]
  );

  // Only the newest event is needed, so a single pass instead of sorting the whole list.
  const latestEvent = useMemo(
    () =>
      maxBy(
        (Array.isArray(data) ? data : []).filter(isTemperatureEvent),
        (event) => new Date(get(event, EVENT_PROPERTIES.TIMESTAMP, 0)).getTime() || 0
      ),
    [data]
  );

  const openHistoryModal = () => {
    openDashboardModal({
      title: (
        <div className="recent-activity-modal-title">
          <TemperatureIcon className="recent-activity-modal-title-icon" />
          <span>{trans('ENVIRONMENTAL')}</span>
        </div>
      ),
      content: (
        <ModalContent
          useruuid={useruuid}
          deviceUuids={deviceUuids}
          timezone={timezone}
          is24HourFormat={is24HourFormat}
          locale={locale}
          measurementSystem={measurementSystem}
        />
      ),
    });
  };

  const handleCardClick = (e) => {
    e.preventDefault();
    openHistoryModal();
  };

  const handleCardKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      openHistoryModal();
    }
  };

  if (isLoading) return <LoadingCard />;

  const location = get(latestEvent, EVENT_PROPERTIES.LOCATION, '');
  const timestamp = get(latestEvent, EVENT_PROPERTIES.TIMESTAMP, '');
  const dateLabel = getDateLabel(timestamp, trans, timezone, locale);
  const formattedTime = formatTime(timestamp, is24HourFormat, timezone);
  const temperatureLabel = `${trans('INDOORTEMPERATURE')}: ${getTemperatureLabel(
    latestEvent,
    LATEST_EVENT_TEMPERATURE_FIELDS,
    measurementSystem
  )}`;

  const trendPercent = get(trendData, 'trendPercent', null);
  const isUp = trendPercent > 0;
  const isDown = trendPercent < 0;
  const displayPercent = trendPercent !== null ? Math.abs(trendPercent) : null;

  const CardTitle = (
    <div className="recent-activity-card-header">
      <TemperatureIcon className="recent-activity-card-icon" />
      <h4 className="recent-activity-card-title">{trans('ENVIRONMENTAL')}</h4>
    </div>
  );

  return (
    <Card
      className="recent-activity-card"
      onClick={handleCardClick}
      onKeyDown={handleCardKeyDown}
      role="button"
      tabIndex={0}
      hoverable
      variant="borderless"
      title={CardTitle}
    >
      <div className="recent-activity-card-body">
        <p className="recent-activity-time">
          {dateLabel} {formattedTime}
        </p>
        {location && <p className="recent-activity-context">{location}</p>}
        <p className="recent-activity-data-value">{temperatureLabel}</p>
      </div>
      <hr className="recent-activity-divider" />
      <div className="recent-activity-footer-section">
        <p className="recent-activity-footer-label">{trans('DAILY_AVG_TEMPERATURE')}</p>
        <div className="recent-activity-card-footer">
          <span className="recent-activity-comparison">{trans('YESTERDAY_VS_LAST_WEEK_AVG')}</span>
          {displayPercent !== null ? (
            <span className={`recent-activity-trend ${isUp ? 'up' : isDown ? 'down' : 'neutral'}`}>
              {isUp ? (
                <UpArrowIcon className="trend-arrow" />
              ) : isDown ? (
                <DownArrowIcon className="trend-arrow" />
              ) : (
                <RightArrowIcon className="trend-arrow" />
              )}
              <span>{displayPercent}%</span>
            </span>
          ) : (
            <span className="recent-activity-trend neutral">-</span>
          )}
        </div>
      </div>
    </Card>
  );
};

SmartHomeEnvironmentalCard.propTypes = {
  useruuid: PropTypes.string,
  timezone: PropTypes.string,
  is24HourFormat: PropTypes.bool,
  locale: PropTypes.string,
  measurementSystem: PropTypes.string,
  accessories: PropTypes.arrayOf(PropTypes.object),
  data: PropTypes.arrayOf(PropTypes.object),
  isLoading: PropTypes.bool,
  trendData: PropTypes.object,
};
